
import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowUpRight, Circle, Terminal, Fingerprint, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import QuantumField from '../components/QuantumField';
import TerminalSearch from '../components/TerminalSearch';

// --- CIPHER TEXT REVEAL ---
const CipherReveal: React.FC<{ 
    text: string; 
    className?: string; 
    active: boolean; 
    delay?: number;
}> = ({ text, className, active, delay = 0 }) => {
    const [display, setDisplay] = useState(active ? '' : text.replace(/./g, '0'));
    const chars = "0123456789!@#$%^&*()_+-=[]{}|;:,.<>?/";

    useEffect(() => {
        if (!active) return;

        let iteration = 0;
        let interval: any = null;

        const startTimeout = setTimeout(() => {
            interval = setInterval(() => {
                setDisplay(prev => 
                    text.split("").map((letter, index) => {
                        if (index < iteration) return text[index];
                        return chars[Math.floor(Math.random() * chars.length)];
                    }).join("")
                );

                if (iteration >= text.length) clearInterval(interval);
                iteration += 1 / 4; 
            }, 30); 
        }, delay);

        return () => {
            clearTimeout(startTimeout);
            if (interval) clearInterval(interval);
        };
    }, [text, active, delay]);

    return <span className={className}>{active ? display : ""}</span>;
};

// --- INTERSECTION VISUAL ---
const IntersectionVisual: React.FC = () => {
    return (
        <div className="w-full py-12 md:py-16 flex items-center justify-center gap-4 md:gap-12 opacity-0 animate-fade-in" style={{ animationDelay: '1.2s' }}>
            <div className="hidden md:flex flex-1 items-center justify-end relative group">
                <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-black animate-line-grow origin-right"></div>
                <div className="absolute right-0 -top-8 flex flex-col items-end opacity-60 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-[9px] font-mono text-gray-500 tracking-[0.3em] uppercase mb-1">Bio.Logic</span>
                    <Fingerprint size={16} className="text-black animate-pulse" />
                </div>
            </div>
            <div className="relative flex-shrink-0 w-24 h-24 flex items-center justify-center">
                <div className="absolute inset-0 border border-gray-100 rounded-full animate-ping opacity-30" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-0 border border-dashed border-gray-300 rounded-full animate-spin-slow opacity-60"></div>
                <div className="absolute inset-4 border-t border-b border-black rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '4s' }}></div>
                <div className="relative z-10 w-4 h-4 bg-black rounded-full animate-pulse shadow-[0_0_15px_rgba(0,0,0,0.5)]"></div>
                <div className="absolute top-0 bottom-0 w-px bg-gray-200"></div>
                <div className="absolute left-0 right-0 h-px bg-gray-200"></div>
            </div>
            <div className="flex-1 flex items-center justify-start relative group">
                <div className="absolute left-0 -top-8 flex flex-col items-start opacity-60 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-[9px] font-mono text-gray-500 tracking-[0.3em] uppercase mb-1">Synth.Etic</span>
                    <Activity size={16} className="text-black animate-pulse" />
                </div>
                <div className="w-full h-[1px] bg-gradient-to-l from-transparent via-gray-300 to-black animate-line-grow origin-left relative overflow-hidden">
                     <div className="absolute inset-0 w-full h-full bg-[repeating-linear-gradient(90deg,transparent,transparent_4px,white_4px,white_8px)] opacity-50"></div>
                </div>
            </div>
        </div>
    );
};

// --- CHAPTER ITEM ---
const ChapterItem: React.FC<{ 
    id: string; 
    title: string; 
    desc: string; 
    link: string; 
    index: number;
}> = ({ id, title, desc, link, index }) => {
    return (
        <Link to={link} className="group block border-t border-gray-200 py-12 relative overflow-hidden transition-all duration-700 hover:border-black">
            <div className="flex flex-col md:flex-row md:items-baseline justify-between relative z-10">
                <div className="flex items-baseline gap-8">
                    <span className="text-xs font-bold font-sans text-gray-300 group-hover:text-black transition-colors duration-500">
                        {id}
                    </span>
                    <h2 className="text-4xl md:text-7xl font-serif text-gray-300 group-hover:text-black transition-colors duration-500 leading-none">
                        {title}
                    </h2>
                </div>
                
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-4 group-hover:translate-y-0 hidden md:block">
                    <ArrowUpRight size={48} className="font-thin" />
                </div>
            </div>

            <div className="max-h-0 group-hover:max-h-40 overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]">
                <div className="pt-6 md:pl-20 max-w-2xl">
                    <p className="text-gray-500 font-sans text-sm md:text-base leading-relaxed tracking-wide">
                        {desc}
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black">
                        <span className="w-8 h-px bg-black"></span>
                        Esplora Capitolo
                    </div>
                </div>
            </div>
            
            <div className="absolute inset-0 bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 transform scale-y-0 group-hover:scale-y-100 origin-center"></div>
        </Link>
    );
}

const Home: React.FC = () => {
  const [mounted, setMounted] = useState(false); 
  const today = new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
      // Trigger animations on mount
      setMounted(true);
  }, []);

  const chapters = [
    { id: '01', title: 'Tecnologia', desc: 'Il substrato fisico della nuova realtà. Hardware quantistico, reti neurali e automazione.', link: '/atlante' },
    { id: '02', title: 'Filosofia', desc: 'Indagine sulla coscienza. Cosa resta dell\'identità umana nell\'era della riproducibilità tecnica?', link: '/manifesto' },
    { id: '03', title: 'Etica', desc: 'I nuovi confini del bene e del male. Bias algoritmici, diritti digitali e responsabilità.', link: '/articoli' },
    { id: '04', title: 'Società', desc: 'L\'impatto sul tessuto civile. Educazione, lavoro e relazioni nel mondo iperconnesso.', link: '/contatti' }
  ];

  return (
    <div className="w-full min-h-screen text-black overflow-x-hidden font-sans bg-white selection:bg-black selection:text-white">
      
      <QuantumField />

      {/* HERO SECTION */}
      <section className="min-h-screen flex flex-col justify-center px-6 md:px-12 relative pt-32 md:pt-48 pb-20">
        <div className="max-w-[1800px] mx-auto w-full">
            
            <div className={`mb-12 transition-all duration-1000 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-black rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">
                        Atlante Digitale — {today}
                    </span>
                </div>
            </div>

            <div className="flex flex-row items-baseline justify-start gap-x-2 md:gap-x-4 mb-12 md:mb-16 mix-blend-multiply relative z-10 cursor-default select-none whitespace-nowrap overflow-visible">
                <div className={`text-[8vw] md:text-[8vw] font-mono font-bold text-black tracking-tighter animate-pulse transition-all duration-1000 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
                    &gt;_
                </div>
                <h1 className="text-[8vw] md:text-[8vw] leading-none flex items-baseline gap-x-2 md:gap-x-4">
                    <CipherReveal text="CYBER" active={mounted} className="font-sans font-extrabold tracking-tighter" delay={100} />
                    <CipherReveal text="UMANITÀ" active={mounted} className="font-sans font-medium italic tracking-tight" delay={600} />
                </h1>
            </div>

            <div className="w-full mx-auto">
               <IntersectionVisual />
            </div>

            <div className={`flex flex-col items-center text-center gap-10 mt-12 transition-all duration-1000 delay-500 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="max-w-3xl mx-auto space-y-4">
                    <p className="text-xl md:text-3xl font-serif font-medium text-black leading-tight">
                        Proteggiamo il digitale; custodiamo l’umano.
                    </p>
                    <p className="text-base md:text-xl text-gray-500 font-sans font-light leading-relaxed max-w-2xl mx-auto">
                        Cybersecurity, intelligenza artificiale ed etica per una tecnologia che amplifica la coscienza, non la dipendenza.
                    </p>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                     <Link to="/atlante" className="group relative px-8 py-4 bg-black overflow-hidden border border-black shadow-lg hover:shadow-xl transition-all">
                        <div className="absolute inset-0 w-full h-full bg-gray-900 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                        <span className="relative z-10 text-xs font-bold uppercase tracking-widest text-white flex items-center gap-2">
                            Entra in Atlante <ArrowRight size={14} />
                        </span>
                     </Link>

                     <Link to="/manifesto" className="group relative px-8 py-4 bg-transparent border border-black overflow-hidden">
                        <div className="absolute inset-0 w-full h-full bg-black transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></div>
                        <span className="relative z-10 text-xs font-bold uppercase tracking-widest text-black group-hover:text-white transition-colors flex items-center gap-2">
                            Leggi il Manifesto
                        </span>
                     </Link>
                </div>
            </div>

        </div>
      </section>

      {/* CHAPTERS */}
      <section className="px-6 md:px-12 pb-32">
          <div className="max-w-[1800px] mx-auto">
              <div className="flex items-center gap-4 mb-12 opacity-50">
                  <Circle size={8} className="fill-black" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Indice dei Contenuti</span>
              </div>

              <div>
                  {chapters.map((chapter, idx) => (
                      <div key={chapter.id} className={`transition-all duration-1000 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${800 + (idx * 150)}ms` }}>
                          <ChapterItem id={chapter.id} title={chapter.title} desc={chapter.desc} link={chapter.link} index={idx} />
                      </div>
                  ))}
                  <div className="border-t border-gray-200"></div>
              </div>
          </div>
      </section>

      {/* TERMINAL */}
      <section className="py-20 px-6 md:px-12 bg-gray-50 border-t border-gray-100 relative z-20">
          <div className="max-w-[1600px] mx-auto">
              <div className="flex items-center justify-between mb-8 opacity-60 hover:opacity-100 transition-opacity">
                   <div className="flex items-center gap-3">
                      <Terminal size={14} className="text-black" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black">
                          Terminale Operativo
                      </span>
                  </div>
                  <span className="text-[9px] font-mono text-gray-500">[INTERACTIVE_SHELL]</span>
              </div>
              
              <div className="w-full h-[450px] md:h-[550px] shadow-2xl border border-gray-200 bg-white relative overflow-hidden group">
                  <TerminalSearch />
              </div>

              <div className="mt-6 text-center">
                  <p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">
                      Accesso al Database Semantico • Digitare 'help' per i comandi
                  </p>
              </div>
          </div>
      </section>

      <section className="py-20 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-6 text-center">
              <p className="text-2xl md:text-4xl font-serif italic text-gray-400 leading-tight mb-8">
                  "Non temiamo le macchine che pensano.<br/>
                  Temiamo gli uomini che pensano come macchine."
              </p>
              <Link to="/atlante" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-b border-black pb-1 hover:text-gray-600 transition-colors">
                  Accedi al Database Completo <ArrowRight size={14} />
              </Link>
          </div>
      </section>

    </div>
  );
};

export default Home;
